/**
 * Playbook catalog.
 *
 * The two facts an operator needs before opening one are the blast radius and whether it runs
 * automatically, so those are on the card rather than inside it. A playbook that can change a
 * shared control without anyone starting it is a different kind of object from one that posts a
 * note, and the list should say so.
 */

import { useNavigate } from 'react-router-dom'
import { api, type Playbook } from '../api'
import { useSession } from '../App'
import { Async, Card, Chip, Empty, Mono, Note, useAsync } from '../components/ui'

export default function Playbooks() {
  const navigate = useNavigate()
  const { me } = useSession()
  const playbooks = useAsync<Playbook[]>(() => api.get<Playbook[]>('/playbooks'), [me.tenant])

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Playbooks</h1>
          <div className="sub">
            Response as a graph, not a script. Every step comes from the action catalog, so what
            a playbook can do to the estate is answerable by reading it rather than by reading code.
          </div>
        </div>
      </div>

      <Async state={playbooks} empty={<Card><Empty title="No playbooks" /></Card>}>
        {(list) => (
          <div className="grid cols-2">
            {list.map((p) => {
              const nodeCount = p.nodes.length
              const approvals = p.nodes.filter((n) => n.kind === 'approval').length
              return (
                <Card
                  key={p.id}
                  title={
                    <span className="row" style={{ gap: 8 }}>
                      {p.name}
                      {!p.enabled && <Chip tone="neutral">disabled</Chip>}
                    </span>
                  }
                  subtitle={<Mono>{p.id}</Mono>}
                  actions={<button className="sm" onClick={() => navigate(`/playbooks/${p.id}`)}>Open</button>}
                >
                  <p className="small muted">{p.description}</p>

                  {p.errors.length > 0 && (
                    <div style={{ marginBottom: 10 }}>
                      <Note tone="danger">
                        This playbook will not run: {p.errors[0]}
                        {p.errors.length > 1 && ` (+${p.errors.length - 1} more)`}
                      </Note>
                    </div>
                  )}

                  <div className="row wrap" style={{ marginBottom: 10 }}>
                    <Chip
                      tone={
                        p.max_blast_radius === 'estate' ? 'danger' : p.max_blast_radius === 'group' ? 'warn' : 'neutral'
                      }
                      title="The widest effect any action in this playbook can have"
                    >
                      {p.max_blast_radius} blast radius
                    </Chip>
                    <Chip tone={p.trigger.automatic ? 'warn' : 'outline'}>
                      {p.trigger.automatic ? 'runs automatically' : 'started by a person'}
                    </Chip>
                    {approvals > 0 && <Chip tone="ok">{approvals} approval gate(s)</Chip>}
                    {p.trigger.min_severity && <Chip tone="outline">≥ {p.trigger.min_severity}</Chip>}
                  </div>

                  <dl className="kv">
                    <dt>Steps</dt>
                    <dd>
                      {nodeCount} node(s), {p.edges.length} wire(s)
                    </dd>
                    <dt>Techniques</dt>
                    <dd className="small">{p.trigger.techniques.join(', ') || '—'}</dd>
                    <dt>Integrations</dt>
                    <dd className="small">
                      {p.required_integrations.length ? (
                        p.required_integrations.map((i) => (
                          <Chip key={i} tone="outline">
                            {i}
                          </Chip>
                        ))
                      ) : (
                        'none'
                      )}
                    </dd>
                  </dl>

                  {p.required_integrations.length > 0 && (
                    <Note>
                      No integrations are configured in this deployment, so containment steps run in
                      simulation and say so in the channel. Nothing is changed on the estate.
                    </Note>
                  )}
                </Card>
              )
            })}
          </div>
        )}
      </Async>
    </>
  )
}
